import { memo, ReactNode, useEffect, useState } from 'react';

import { useOutsideClick } from 'shared/hooks/useOutsideClick';
import { Box } from 'shared/ui/Box';
import { FormCategoryTitle } from 'shared/ui/FormComponents/FormCategoryTitle';
import { Portal } from 'shared/ui/Portal';

import { BackgroundProps, type MaxHeightProps, MaxWidthProps } from 'styled-system';
import { Icon } from '../Icon';
import { CloseButton, ContentWrapper, ModalTitleWrapper, ModalWrapper, Overlay } from './styled';

export type BaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  disable?: boolean;
  background?: BackgroundProps['background'];
  maxHeight?: MaxHeightProps['maxHeight'];
  isHideScroll?: boolean;
  height?: string | number;
  maxWidth?: MaxWidthProps['maxWidth'];
};

export type ExternalModalProps = Omit<BaseModalProps, 'isOpen' | 'onClose' | 'children'>;

export const BaseModal = memo(
  ({
    children,
    isOpen,
    onClose,
    title,
    maxHeight,
    maxWidth,
    background,
    disable = false,
    isHideScroll = false,
    height,
  }: BaseModalProps) => {
    const [isVisible, setIsVisible] = useState(isOpen);
    const { refElement } = useOutsideClick<HTMLDivElement>(() => {
      if (disable) return;
      onClose();
    });

    useEffect(() => {
      if (isOpen) {
        setIsVisible(true);

        return;
      }

      const timeout = setTimeout(() => setIsVisible(false), 300);

      return () => clearTimeout(timeout);
    }, [isOpen]);

    if (!isVisible) return null;

    return (
      <Portal>
        <Overlay $isOpen={isOpen}>
          <ModalWrapper
            ref={refElement}
            $isOpen={isOpen}
            role='dialog'
            max={maxHeight}
            maxWidth={maxWidth}
            height={height}
            background={background}
          >
            <Box>
              <CloseButton onClick={onClose} disabled={disable}>
                <Icon name='close' width={24} height={24} />
              </CloseButton>
              {title && (
                <ModalTitleWrapper>
                  <FormCategoryTitle>{title}</FormCategoryTitle>
                </ModalTitleWrapper>
              )}
            </Box>

            <ContentWrapper isHideScroll={isHideScroll}>{children}</ContentWrapper>
          </ModalWrapper>
        </Overlay>
      </Portal>
    );
  }
);
